import { useMutation, useQueryClient } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';
import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { ScheduleInput } from '../features/offerModal/types';
import {
  buildScheduleOptimisticUpdateData,
  buildSchedulePatchData,
  buildSchedulePostData,
} from '../features/offerModal/utils';
import { mapFrequencyToApiFormat, mapFrequencyToCacheFormat } from './frequencyUtils';
import { determineOfferStatus, determineOptimisticIsEnabled } from './offerStatusUtils';

type Referrer = paths['/offers/static']['post']['requestBody']['content']['application/json']['referrer'];

export interface SubmitStaticOfferParams {
  rate: number;
  marketUuid: string;
  supplierDivisionUuid: string;
  expireOn?: string | null;
  schedule?: ScheduleInput | null;
  frequency?: 'one-time' | 'recurring';
  referrer?: Referrer;
}

const useSubmitStaticOffer = () => {
  const queryClient = useQueryClient();
  const { data: takerMarkets } = useTakerMarkets();
  const { refetchOneTakerMarket } = useRefetchStatsQueries();
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const { addPendingMarkets } = usePendingOfferPollingStore();

  return useMutation({
    mutationKey: ['submitStaticOffer'],
    mutationFn: async (variables: SubmitStaticOfferParams) => {
      const { rate, marketUuid, supplierDivisionUuid, expireOn, schedule, frequency, referrer } = variables;
      const takerMarket = takerMarkets?.find(
        (tm) => tm.marketUuid === marketUuid && tm.takerDivisionUuid === supplierDivisionUuid
      );
      const uuid = takerMarket?.offerConfig.uuid;

      const response = uuid
        ? await apiClient.patch('api/supplier-experience/offers/static', {
            json: {
              groupingKey: 'SUPPLIER_MARKET',
              status: determineOfferStatus(schedule),
              rateInformation: {
                rateType: 'APR',
                rate: rate,
              },
              expirationDate: expireOn,
              schedule: buildSchedulePatchData(schedule, takerMarket),
              frequency,
              offers: [
                {
                  makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                  uuid,
                },
              ],
              referrer,
            } satisfies paths['/offers/static']['patch']['requestBody']['content']['application/json'],
          })
        : await apiClient.post('api/supplier-experience/offers/static', {
            json: {
              groupingKey: 'SUPPLIER_MARKET',
              offerType: 'static',
              frequency: mapFrequencyToApiFormat(frequency),
              rateType: 'APR',
              rate: rate,
              expirationDate: expireOn ?? undefined, // Allow null to coalesce to undefined for POST because this is a net new offer.
              schedule: buildSchedulePostData(schedule),
              supplierMarkets: [
                {
                  makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                  marketUuid,
                  supplierDivisionUuid,
                },
              ],
              referrer,
            } satisfies paths['/offers/static']['post']['requestBody']['content']['application/json'],
          });

      if (!uuid) {
        if (response.status === 201) {
          const body =
            await response.json<paths['/offers/static']['post']['responses']['201']['content']['application/json']>();
          return { status: response.status, offerUuid: body.data[0]?.uuid };
        }

        if (response.status === 202) {
          const body =
            await response.json<paths['/offers/static']['post']['responses']['202']['content']['application/json']>();
          return { status: response.status, offerUuid: body.data.offers[0]?.uuid };
        }
      }

      return { status: response.status };
    },
    onSuccess: (result, variables) => {
      const tmMeta = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      if (tmMeta?.marketUuid && tmMeta?.takerDivisionId) {
        // If placing first offer, use returned offer uuid from API response to update cache
        // User can interact with offer before stats and offer refetches
        const offerUuid = tmMeta?.offerConfig.uuid ?? result.offerUuid;

        const status = determineOfferStatus(variables.schedule);

        // update the cache with the returned data
        queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) =>
          (prevTakerMarkets ?? []).map((tm) => ({
            ...(tm.marketUuid === variables.marketUuid && tm.takerDivisionUuid === variables.supplierDivisionUuid
              ? {
                  ...tm,
                  offerConfig: {
                    ...tm.offerConfig,
                    maxApr: variables.rate,
                    isEnabled: determineOptimisticIsEnabled(status, tmMeta.offerConfig.isEnabled ?? false),
                    uuid: offerUuid,
                    frequency: variables.frequency
                      ? mapFrequencyToCacheFormat(variables.frequency)
                      : tm.offerConfig.frequency,
                  },
                  disableAfterMarketClearsDate: variables.expireOn ?? null,
                  offerSchedule: buildScheduleOptimisticUpdateData(variables.schedule, tm),
                }
              : tm),
          }))
        );

        if (result.status === 202) {
          addPendingMarkets([
            {
              marketUuid: tmMeta.marketUuid,
              takerDivisionUuid: tmMeta.takerDivisionUuid,
              makerOrganizationUuid: tmMeta.makerOrganizationUuid,
            },
          ]);
        } else {
          // offer resolved synchronously, refetch immediately
          trackEnterpriseEvent('polling-update::in-time', {
            marketUuid: tmMeta.marketUuid,
            takerDivisionUuid: tmMeta.takerDivisionUuid,
            makerOrganizationUuid: tmMeta.makerOrganizationUuid,
          });
          refetchOneTakerMarket({ marketUuid: tmMeta.marketUuid, takerId: tmMeta.takerDivisionId });
        }
      }
    },

    onError: (error, variables) => {
      const takerMarket = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      addError('useSubmitStaticOffer', {
        error,
        variables,
        seaOfferUuid: takerMarket?.offerConfig.uuid,
      });
    },
  });
};

export default useSubmitStaticOffer;
